/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListIdentityProviderConnectionApplicationsRequest,
  ListIdentityProviderConnectionApplicationsSecurity,
} from "../models/operations/listidentityproviderconnectionapplications.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildIdentityProviderConnectionApplicationsQuery,
  IdentityProviderConnectionApplicationsQueryData,
  prefetchIdentityProviderConnectionApplications,
  queryKeyIdentityProviderConnectionApplications,
} from "./identityProviderConnectionApplications.core.js";
export {
  buildIdentityProviderConnectionApplicationsQuery,
  type IdentityProviderConnectionApplicationsQueryData,
  prefetchIdentityProviderConnectionApplications,
  queryKeyIdentityProviderConnectionApplications,
};

export type IdentityProviderConnectionApplicationsQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listApplications identityProviderConnections
 *
 * @remarks
 * List the applications snapshot for the connection with live assignment counts and the last reconcile run. The connection must be verified. Requires org:admin.
 */
export function useIdentityProviderConnectionApplications(
  request: ListIdentityProviderConnectionApplicationsRequest,
  security?: ListIdentityProviderConnectionApplicationsSecurity | undefined,
  options?: QueryHookOptions<
    IdentityProviderConnectionApplicationsQueryData,
    IdentityProviderConnectionApplicationsQueryError
  >,
): UseQueryResult<
  IdentityProviderConnectionApplicationsQueryData,
  IdentityProviderConnectionApplicationsQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildIdentityProviderConnectionApplicationsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listApplications identityProviderConnections
 *
 * @remarks
 * List the applications snapshot for the connection with live assignment counts and the last reconcile run. The connection must be verified. Requires org:admin.
 */
export function useIdentityProviderConnectionApplicationsSuspense(
  request: ListIdentityProviderConnectionApplicationsRequest,
  security?: ListIdentityProviderConnectionApplicationsSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    IdentityProviderConnectionApplicationsQueryData,
    IdentityProviderConnectionApplicationsQueryError
  >,
): UseSuspenseQueryResult<
  IdentityProviderConnectionApplicationsQueryData,
  IdentityProviderConnectionApplicationsQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildIdentityProviderConnectionApplicationsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setIdentityProviderConnectionApplicationsData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      id: string;
      includeRemoved?: boolean | undefined;
      gramSession?: string | undefined;
    },
  ],
  data: IdentityProviderConnectionApplicationsQueryData,
): IdentityProviderConnectionApplicationsQueryData | undefined {
  const key = queryKeyIdentityProviderConnectionApplications(...queryKeyBase);

  return client.setQueryData<IdentityProviderConnectionApplicationsQueryData>(
    key,
    data,
  );
}

export function invalidateIdentityProviderConnectionApplications(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      id: string;
      includeRemoved?: boolean | undefined;
      gramSession?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "identityProviderConnections",
      "listApplications",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllIdentityProviderConnectionApplications(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "identityProviderConnections",
      "listApplications",
    ],
  });
}
